import { logWarning } from "../services/logging";
import { getValueFromPropertyPath } from "./condition-helpers";

const replaceRegex = /({{[^}]+}})/g;

type FunctionInput = {
  value: any;
  parameters: string;
  originalPattern: string;
};
type FunctionResult = {
  isSuccess: boolean;
  result: any;
};

const functionsMap: {
  [key: string]: (input: FunctionInput) => FunctionResult;
} = {
  slice: ({ value, parameters, originalPattern }) => {
    if (typeof value !== "string") {
      logWarning(
        `Tried to apply 'slice' string template function from pattern ${originalPattern} on a non-string value: ${value}`
      );
      return { isSuccess: false, result: null };
    }
    if (parameters == null || parameters.trim().length === 0) {
      // If no parameters are provided, just continue with the original string
      return { isSuccess: true, result: value };
    }
    const params = parameters.split(",").map((s) => parseInt(s.trim(), 10));

    if (params.some((p) => isNaN(p) || p < 0)) {
      logWarning(
        `Tried to apply 'slice' string template function with invalid parameters from pattern ${originalPattern} on value: ${value}`
      );
      return { isSuccess: false, result: null };
    }

    if (params.length === 1) {
      value = value.slice(params[0]);
    } else if (params.length >= 2) {
      value = value.slice(params[0], params[0] + params[1]);
    }
    return { isSuccess: true, result: value };
  },
};

// We should later replace all of this template handling with liquidjs or similar library with same syntax,
// but as long as we want to support compatibility mode we keep this simple implementation which we have
// full control over.
export function getValueWithAppliedTemplate(template: string, viewModel: any) {
  if (template) {
    const result = template.replace(replaceRegex, (capture) => {
      if (!capture || capture.length < 4) {
        return null;
      }
      const split = capture
        .substring(2, capture.length - 2)
        .trim()
        .split("|")
        .map((s) => s?.trim())
        .filter((s) => s?.length > 0);
      if (split.length === 0) {
        return null;
      }
      let value = getValueFromPropertyPath(split[0], viewModel);
      for (let i = 1; i < split.length; i++) {
        try {
          const funcNameAndParams = split[i]
            .trim()
            .split(":")
            .filter((s) => s?.trim().length > 0);
          if (funcNameAndParams.length > 0) {
            const funcName = funcNameAndParams[0].trim().toLowerCase();
            const funcParams = funcNameAndParams.slice(1).join(":").trim();
            const func = functionsMap[funcName];
            if (!func) {
              logWarning(
                `Tried to apply unknown string template function '${funcName}' from pattern ${capture} on value: ${value}`
              );
              return null;
            }
            const funcResult = func({
              value: value,
              parameters: funcParams,
              originalPattern: capture,
            });
            if (!funcResult.isSuccess) {
              return null;
            }
            value = funcResult.result;
          }
        } catch (e) {
          logWarning(
            `Failed applying template functions from index ${i} from pattern ${capture} on value: ${value}`,
            e
          );
          return null;
        }
      }
      return value;
    });
    return result;
  } else {
    return null;
  }
}
